import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { AppointmentRescheduleReason, EventType, RouteParameters, SearchParameters, TechSetupFailureReason } from '../../../../constants'
import { Route } from '../../../../constants/route'
import {
  AppointmentLocationState,
  AppointmentRescheduleLocationState,
  useAppointment,
  useCountry,
  useEventTracking,
  useFeatureFlags,
  useFormAnalytics,
  useNotification,
  useRescheduleContext,
} from '../../../../hooks'
import { useAppointmentsService } from '../../../../hooks/services'
import { Appointment, AppointmentRescheduleFormData, Shop } from '../../../../types'
import { appendSearchParamsToPathname, getDeliveryModelFromJourney, hydratePathname, logFactory, pick } from '../../../../util'
import { Form as SharedForm } from '../../../forms'
import { PrimaryAppointmentRescheduleReason } from '../utils'
import { Content } from './Content'
import { validationSchema } from './validationSchema'

interface Props {
  defaultValues: AppointmentRescheduleFormData
}

const log = logFactory('RescheduleForm')

const appointmentAndShopToAppointmentLocationState = (appointment: Appointment, shop: Shop | undefined) => {
  const { type, country, customer, duration_in_minutes, journey, opportunity_gid, resources } = appointment
  const { salesforceId: customer_gid } = customer
  const shop_gid = shop?.gid ?? appointment.shop_gid
  const { appointment_type } = type

  const resource_ids = resources.map(({ id }) => id)

  const state: AppointmentLocationState = {
    appointment_type,
    country,
    customer_gid,
    duration_in_minutes: duration_in_minutes as number,
    journey,
    opportunity_gid: opportunity_gid as string,
    resource_ids,
    shop_gid,
  }

  return state
}

const Form = ({ defaultValues }: Props) => {
  const [shop, setShop] = useState<Shop>()
  const { appointment, isLoading: isAppointmentLoading } = useAppointment()
  const country = useCountry()
  const { getShops, isReady: isServiceReady } = useAppointmentsService()
  const { search } = useLocation()
  const navigate = useNavigate()
  const rescheduleContext = useRescheduleContext()
  const { track: trackEvent } = useEventTracking()
  const { notify } = useNotification()
  const { onErrorsChange } = useFormAnalytics<AppointmentRescheduleFormData>()

  const deliveryModel = useMemo(() => getDeliveryModelFromJourney(appointment?.journey), [appointment?.journey])
  const [isPooledSchedulingOn] = useFeatureFlags<boolean>(['POOLED_SCHEDULING'])
  const schema = useMemo(
    () => validationSchema(country, deliveryModel, isPooledSchedulingOn),
    [country, deliveryModel, isPooledSchedulingOn]
  )

  useEffect(() => {
    trackEvent(EventType.APPOINTMENT_RESCHEDULE_FORM_VIEWED)
  }, [])

  useEffect(() => {
    const providerShopId = appointment?.resources[0]?.shop_id

    if (!providerShopId || !isServiceReady || shop) {
      return
    }

    const loadShop = async () => {
      const { data } = await getShops({
        id: providerShopId,
      })

      const [found] = data

      setShop(found)
    }

    loadShop()
  }, [appointment, getShops, isServiceReady, setShop, shop])

  const onError = useCallback((errors) => onErrorsChange(errors), [onErrorsChange])

  const onSubmit = useCallback(
    async (data: AppointmentRescheduleFormData): Promise<void> => {
      const primaryReason = data.reason === PrimaryAppointmentRescheduleReason.TECH_SETUP_FAILED ? data.techSetupFailReason : data.reason
      const description = data.description ? (data.description as string) : undefined
      const techSetupFailReason = data.techSetupFailReason as TechSetupFailureReason

      // cast to AppointmentCancellationReason, because it will come in as PrimaryAppointmentCancellationReason, but always leave as this.
      const reason = primaryReason as AppointmentRescheduleReason

      const formData: AppointmentRescheduleFormData = {
        description,
        reason,
        techSetupFailReason,
      }

      log('✍️ Passing form data to next page...', formData)

      const pathname = hydratePathname(Route.APPOINTMENT_RESCHEDULE_NEW, {
        [RouteParameters.COUNTRY]: appointment?.country,
        [RouteParameters.APPOINTMENT_ID]: appointment?.id,
      })
      const state = appointmentAndShopToAppointmentLocationState(appointment as Appointment, shop)

      const rescheduleState: AppointmentRescheduleLocationState = {
        ...state,
        description,
        reason,
      }

      rescheduleContext.setContext(formData)

      const searchParams = new URLSearchParams(search)

      if (appointment?.id) {
        searchParams.set(SearchParameters.APPOINTMENT_ID, appointment.id)
      }

      const actualPathname = appendSearchParamsToPathname({ pathname, searchParams })

      navigate(actualPathname, { state: rescheduleState })
    },
    [appointment, navigate, notify, rescheduleContext, search, shop]
  )

  const actualDefaultValues = {
    ...defaultValues,
    ...pick(rescheduleContext, ['appointment', 'description', 'reason', 'techSetupFailReason']),
  }

  return (
    <SharedForm<AppointmentRescheduleFormData>
      key={String(deliveryModel)}
      defaultValues={actualDefaultValues}
      disabled={isAppointmentLoading}
      onError={onError}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      <Content deliveryModel={deliveryModel} isPooledSchedulingOn={isPooledSchedulingOn} />
    </SharedForm>
  )
}

export { Form }
